import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { crmSupabase } from "../../lib/crm-client";
import {
  listMessages,
  postMessage,
  getSetting,
  type ConversationMessage,
} from "../../lib/crm-mutations";

type LeadDetail = {
  id: string;
  full_name: string;
  display_name: string | null;
  status: string;
  is_estrella: boolean;
  how_we_met: string | null;
  notes_md: string | null;
  last_contact_at: string | null;
  last_response_at: string | null;
  estrella_since: string | null;
  category: { name: string; color_hex: string } | null;
  identities: { kind: string; value: string; is_primary: boolean }[];
  next_actions: { id: string; what: string; due_date: string; status: string; priority: number; ball_in_court: string | null }[];
};

export default function CrmLeadDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<LeadDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!crmSupabase || !id) {
      setLoading(false);
      return;
    }
    crmSupabase
      .from("lead")
      .select(
        "id,full_name,display_name,status,is_estrella,how_we_met,notes_md,last_contact_at,last_response_at,estrella_since,category:category_id(name,color_hex),identities:lead_identity(kind,value,is_primary),next_actions:next_action(id,what,due_date,status,priority,ball_in_court)",
      )
      .eq("id", id)
      .single()
      .then(({ data: row, error }) => {
        if (error) console.error(error);
        setData((row as any) ?? null);
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <main className="px-10 py-8 max-w-[900px] mx-auto font-mono text-sm text-concrete">
        Chargement…
      </main>
    );
  }

  if (!data) {
    return (
      <main className="px-10 py-8 max-w-[900px] mx-auto">
        <p className="font-mono text-sm text-concrete">
          Lead introuvable.{" "}
          <Link to="/crm/leads" className="underline text-charcoal">
            Retour à la liste
          </Link>
        </p>
      </main>
    );
  }

  const primaryEmail = data.identities.find((i) => i.kind === "email" && i.is_primary)?.value;
  const phones = data.identities.filter((i) => i.kind === "phone");

  return (
    <main className="px-10 py-8 max-w-[900px] mx-auto">
      <Link
        to="/crm/leads"
        className="font-mono text-xs uppercase text-concrete hover:text-charcoal mb-3 inline-block"
      >
        ← Tous les leads
      </Link>

      <header className="mb-6">
        <h2 className="font-sans text-4xl font-bold tracking-tight text-charcoal flex items-baseline gap-2 flex-wrap">
          {data.is_estrella && <span className="text-lemonade">★</span>}
          {data.full_name}
          {data.category && (
            <span
              className="font-mono text-[11px] uppercase px-2 py-0.5 rounded text-white"
              style={{ backgroundColor: data.category.color_hex }}
            >
              {data.category.name}
            </span>
          )}
          <span className="font-mono text-[11px] uppercase text-concrete">
            {data.status}
          </span>
        </h2>
      </header>

      <section className="bg-white border border-stone rounded-lg p-4 mb-4">
        <h3 className="font-mono text-xs uppercase text-concrete mb-2">Contact</h3>
        <ul className="flex flex-col gap-1 text-sm">
          {primaryEmail && (
            <li>
              <a
                href={`mailto:${primaryEmail}`}
                className="text-charcoal hover:underline font-mono"
              >
                ✉ {primaryEmail}
              </a>
            </li>
          )}
          {phones.map((p) => (
            <li key={p.value}>
              <a
                href={`https://wa.me/${p.value.replace(/\D/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-charcoal hover:underline font-mono"
              >
                ☎ {p.value} · ouvrir WhatsApp ↗
              </a>
            </li>
          ))}
          {!primaryEmail && phones.length === 0 && (
            <li className="text-ash font-mono text-xs">Aucun contact enregistré</li>
          )}
        </ul>
      </section>

      <section className="bg-white border border-stone rounded-lg p-4 mb-4">
        <h3 className="font-mono text-xs uppercase text-concrete mb-2">Contexte</h3>
        {data.how_we_met && (
          <p className="text-sm text-charcoal mb-2">{data.how_we_met}</p>
        )}
        <div className="grid grid-cols-3 gap-3 font-mono text-[10px] uppercase text-concrete">
          <div>
            <div className="text-ash">Dernier contact</div>
            <div className="text-charcoal">
              {data.last_contact_at?.slice(0, 10) ?? "—"}
            </div>
          </div>
          <div>
            <div className="text-ash">Dernière réponse</div>
            <div className="text-charcoal">
              {data.last_response_at?.slice(0, 10) ?? "—"}
            </div>
          </div>
          {data.is_estrella && data.estrella_since && (
            <div>
              <div className="text-ash">Estrella depuis</div>
              <div className="text-charcoal">{data.estrella_since.slice(0, 10)}</div>
            </div>
          )}
        </div>
        {data.notes_md && (
          <p className="text-sm text-charcoal mt-3 italic">{data.notes_md}</p>
        )}
      </section>

      {data.next_actions.length > 0 && (
        <section className="bg-white border border-stone rounded-lg p-4">
          <h3 className="font-mono text-xs uppercase text-concrete mb-2">
            Next actions ({data.next_actions.length})
          </h3>
          <ul className="flex flex-col gap-2">
            {data.next_actions.map((na) => (
              <li
                key={na.id}
                className="border-l-[3px] border-stone pl-3 py-1"
                style={{
                  borderColor:
                    na.status === "late"
                      ? "#dc2626"
                      : na.status === "done"
                        ? "#16a34a"
                        : undefined,
                }}
              >
                <div className="text-sm text-charcoal">{na.what}</div>
                <div className="flex gap-3 font-mono text-[10px] uppercase text-concrete mt-0.5">
                  <span>due {na.due_date}</span>
                  <span>priorité {na.priority}</span>
                  <span>ball: {na.ball_in_court ?? "—"}</span>
                  <span
                    className={
                      na.status === "late"
                        ? "text-red-600 font-bold"
                        : na.status === "done"
                          ? "text-green-600"
                          : "text-charcoal"
                    }
                  >
                    {na.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <ConversationSection leadId={data.id} leadName={data.full_name} />
    </main>
  );
}

// ----------------------------------------------------------------------------
// Conversation thread (Jeremy ↔ assistant) attached to a lead
// ----------------------------------------------------------------------------

function ConversationSection({
  leadId,
  leadName,
}: {
  leadId: string;
  leadName: string;
}) {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [assistantEmail, setAssistantEmail] = useState<string>("eitan@llyll.com");
  const [assistantName, setAssistantName] = useState<string>("Ethan");

  useEffect(() => {
    listMessages(leadId).then(setMessages).catch(console.error);
    getSetting("assistant_email").then((v) => v && setAssistantEmail(v));
    getSetting("assistant_name").then((v) => v && setAssistantName(v));
  }, [leadId]);

  const handleSend = async (alsoEmail: boolean) => {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      const msg = await postMessage({
        lead_id: leadId,
        body,
        sent_by_email: alsoEmail,
      });
      if (msg) setMessages((m) => [...m, msg]);
      setDraft("");
      if (alsoEmail) {
        const subject = encodeURIComponent(`[CRM · ${leadName}] Note de Jeremy`);
        const emailBody = encodeURIComponent(
          `Salut ${assistantName},\n\nÀ propos de ${leadName} dans le CRM :\n\n${body}\n\n— Jeremy\n\n(Note aussi visible dans le dashboard CRM : https://test-crm.videolink.llyll.com/crm/leads/${leadId})`,
        );
        window.open(
          `mailto:${assistantEmail}?subject=${subject}&body=${emailBody}`,
          "_blank",
        );
      }
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l'envoi. Voir console.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="bg-white border border-stone rounded-lg p-4 mt-4">
      <h3 className="font-mono text-xs uppercase text-concrete mb-3">
        Conversation pour {assistantName} ({assistantEmail})
      </h3>

      {messages.length === 0 ? (
        <p className="font-mono text-[11px] text-ash italic mb-3">
          Aucun message. Écris ci-dessous pour démarrer un fil.
        </p>
      ) : (
        <ul className="flex flex-col gap-2 mb-3">
          {messages.map((m) => (
            <li
              key={m.id}
              className={`px-3 py-2 rounded text-sm ${
                m.from_role === "jeremy"
                  ? "bg-ivory text-charcoal"
                  : "bg-charcoal text-ivory"
              }`}
            >
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="font-mono text-[10px] uppercase opacity-60">
                  {m.from_role === "jeremy" ? "Jeremy" : assistantName}
                </span>
                <span className="font-mono text-[10px] opacity-50">
                  {new Date(m.created_at).toLocaleString("fr-FR", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  {m.sent_by_email && " · ✉ envoyé"}
                </span>
              </div>
              <p className="whitespace-pre-wrap">{m.body}</p>
            </li>
          ))}
        </ul>
      )}

      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={`Question ou instruction pour ${assistantName}…`}
        rows={3}
        className="w-full px-3 py-2 bg-ivory border border-stone rounded text-sm focus:outline-none focus:border-charcoal resize-y"
      />
      <div className="flex gap-2 mt-2 justify-end">
        <button
          onClick={() => handleSend(false)}
          disabled={sending || !draft.trim()}
          className="px-3 py-1.5 rounded border border-stone bg-white text-charcoal text-xs font-mono uppercase hover:border-charcoal disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Note dashboard seulement
        </button>
        <button
          onClick={() => handleSend(true)}
          disabled={sending || !draft.trim()}
          className="px-3 py-1.5 rounded bg-charcoal text-ivory text-xs font-mono uppercase hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Note + email à {assistantName}
        </button>
      </div>
    </section>
  );
}
